import { Markdown } from '@/components/ui'
import { useDiagnosticsEnabled } from '../../hooks'
import { IS_ADMIN_MODE } from '../../hooks/useIsAdminMode'
import type {
  ArtifactRecord,
  ContentBlock,
  StepUsage,
  ToolCallStatus,
} from '../../types'
import type { ToolResultStatus } from '../../utils/extractArtifacts'
import { stripCodeBlocks } from '../../utils/stripCodeBlocks'
import ArtifactView from './assistantBubble/ArtifactView'
import {
  PLAIN_PROSE_COMPONENTS,
  PROSE_CLASSES,
} from './assistantBubble/proseClasses'
import {
  buildRenderItems,
  collapseToolGroups,
} from './assistantBubble/renderItems'
import UsageChip from './assistantBubble/UsageChip'
import BootSequence from './BootSequence'
import ThinkingStatus from './ThinkingStatus'
import ToolActivity from './ToolActivity'
import ToolCallBlock from './ToolCallBlock'

// Tools whose raw input/output is the agent's own internals — `read_skill`
// returns skill/system-prompt markdown — so their name and payloads must
// not reach end users. The "Reading skill" chip still shows as a generic
// transparency signal; only the contentful details are withheld unless an
// admin has diagnostics on (admin = non-production deploy, see
// useIsAdminMode). In production this is always concealed.
const SYSTEM_INTERNAL_TOOLS: ReadonlySet<string> = new Set(['read_skill'])

interface AssistantBubbleProps {
  slug: string
  blocks: ContentBlock[]
  toolCallStatuses?: Record<string, ToolCallStatus>
  artifacts?: Record<string, ArtifactRecord>
  toolResults?: Record<string, ToolResultStatus>
  stepUsages?: StepUsage[]
  // Live drafts only: block index where each model step begins. Used to
  // place usage chips at step boundaries instead of after every tool-group.
  // Persisted (non-draft) messages don't need this; each persisted message
  // is itself one step and renders totalUsage at the bottom.
  stepBoundaries?: number[]
  // Per-message totals for the diagnostics chip on persisted assistant
  // messages. `costMicros` comes straight off the server (see
  // ChatMessageSerializer) so the chip doesn't need a client-side
  // pricing table to render.
  totalUsage?: {
    inputTokens: number
    outputTokens: number
    cacheReadInputTokens: number
    cacheCreationInputTokens: number
    costMicros: number
  } | null
  model?: string | null
  isStreaming?: boolean
  showBootHeader?: boolean
}

export default function AssistantBubble({
  slug,
  blocks,
  toolCallStatuses,
  artifacts,
  toolResults,
  stepUsages,
  stepBoundaries,
  totalUsage,
  model,
  isStreaming = false,
  showBootHeader = false,
}: AssistantBubbleProps) {
  const diagnosticsEnabled = useDiagnosticsEnabled()
  const allItems = buildRenderItems(
    blocks,
    toolCallStatuses,
    artifacts,
    toolResults,
    stepBoundaries,
  )
  // Tool-call rows (including the upfront plan checklist) are visible to
  // every user — they're the agent's transparency surface. Step-end markers
  // and their step-based group splits are diagnostics-only: their sole
  // purpose is to anchor UsageChips, so with diagnostics off we collapse
  // them away to keep tool-chip spacing uniform.
  const items = diagnosticsEnabled ? allItems : collapseToolGroups(allItems)
  const lastActualItem = [...allItems]
    .reverse()
    .find((item) => item.kind !== 'step-end')
  const showInlineCaret = isStreaming && lastActualItem?.kind === 'text'

  const usages = diagnosticsEnabled ? (stepUsages ?? []) : []
  // Skill internals are only revealed to admins who have diagnostics on.
  const revealInternalToolDetails = IS_ADMIN_MODE && diagnosticsEnabled

  const bootDone = !isStreaming || allItems.length > 0
  // Hide thinking while the boot-sequence spinner is still animating on the
  // very first message of a session; it owns the visual at that moment.
  const bootSequencePlaying = showBootHeader && !bootDone
  // Show thinking whenever streaming and we're not mid-text — that covers
  // the dead space before the first SSE event lands, plus the gap between
  // tool calls and the next chunk.
  const showThinking =
    isStreaming &&
    !bootSequencePlaying &&
    (lastActualItem === undefined || lastActualItem.kind !== 'text')

  return (
    <div className="flex w-full min-w-0 flex-col gap-3">
      {showBootHeader && <BootSequence done={bootDone} />}
      {items.map((item, index) => {
        const isLast = index === items.length - 1

        if (item.kind === 'text') {
          const trailingCaret = isLast && showInlineCaret
          const content = stripCodeBlocks(item.text)
          const withCaret = trailingCaret
            ? `${content}<span class="streaming-caret" aria-hidden="true"></span>`
            : content
          return (
            <div key={item.key} className={PROSE_CLASSES}>
              <Markdown
                content={withCaret}
                componentsOverride={PLAIN_PROSE_COMPONENTS}
              />
            </div>
          )
        }

        if (item.kind === 'artifact') {
          return (
            <ArtifactView key={item.key} slug={slug} artifact={item.artifact} />
          )
        }

        if (item.kind === 'step-end') {
          // Usage chips live at step boundaries: they render under whatever
          // content the step produced (text, tools, or both). Drives the
          // per-step layout for diagnostics view without anchoring chips to
          // tool-groups that may not exist on every step.
          const usage = usages[item.stepIndex]
          if (!usage) return null
          return <UsageChip key={item.key} usage={usage} />
        }

        return (
          <ToolActivity key={item.key} groupKey={item.key} calls={item.calls}>
            <div className="flex w-full min-w-0 flex-col items-start gap-1">
              {item.calls.map((call) => {
                // Withhold the skill name + raw payloads from non-admins so
                // system internals don't leak; the chip still renders its
                // generic label. With args/result undefined, ToolCallBlock
                // hides its expand panel automatically.
                const concealDetails =
                  SYSTEM_INTERNAL_TOOLS.has(call.name) &&
                  !revealInternalToolDetails
                return (
                  <ToolCallBlock
                    key={call.id}
                    name={call.name}
                    status={call.status}
                    errorCode={call.errorCode}
                    superseded={call.superseded}
                    startedAt={call.startedAt}
                    finishedAt={call.finishedAt}
                    argsSummary={concealDetails ? null : call.argsSummary}
                    resultSummary={concealDetails ? null : call.resultSummary}
                    args={concealDetails ? undefined : call.args}
                    result={concealDetails ? undefined : call.result}
                  />
                )
              })}
            </div>
          </ToolActivity>
        )
      })}

      {diagnosticsEnabled && !stepUsages && totalUsage && (
        <UsageChip
          usage={{
            step: -1,
            model: model ?? null,
            stopReason: null,
            inputTokens: totalUsage.inputTokens,
            outputTokens: totalUsage.outputTokens,
            cacheReadInputTokens: totalUsage.cacheReadInputTokens,
            cacheCreationInputTokens: totalUsage.cacheCreationInputTokens,
            costMicros: totalUsage.costMicros,
          }}
        />
      )}

      {isStreaming && (
        <div className="min-h-4 leading-4">
          {showThinking && <ThinkingStatus />}
        </div>
      )}
    </div>
  )
}
